// AGC: useAppNavigate — shim hook that bridges Screen-based navigation to React Router
// AGC: Components that receive onNavigate callback can use this hook instead
// AGC: Maps Screen names to URL paths, passes navigation data via route state
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Screen, NavigationState } from '../types'

// AGC: Screen → URL path mapping (canonical routes)
const SCREEN_ROUTES: Record<string, string> = {
  // AGC: Main nav
  'dashboard': '/',
  'profile': '/profile',
  'notifications': '/notifications',
  'subscription': '/subscription',

  // AGC: Lotes/Fields
  'lotes-list': '/lotes',
  'fields': '/lotes',
  'novo-lote': '/lotes/new',
  'create-with-gps': '/lotes/new/gps',
  'ai-one-click-lot': '/lotes/new/ai',
  'auto-detect-fields': '/lotes/new/auto-detect',
  'finalizar-lote': '/lotes/finalize',
  'polygon-map': '/lotes/polygon',
  'farm-lots-map': '/lotes/map',
  'crop-suggestions': '/lotes/crop-suggestions',
  'field-task-planning': '/lotes/task-planning',
  // AGC: Parameterized lotes routes — plotId passed via state
  'lot-page': '/lotes/view',
  'plot-detail': '/lotes/detail',
  'lot-images': '/lotes/images',
  'select-crop': '/lotes/select-crop',
  'management-plan': '/lotes/management-plan',

  // AGC: Legacy field creation flow
  'new-plot-1': '/lotes/legacy/step1',
  'new-plot-2': '/lotes/legacy/step2',
  'plot-summary': '/lotes/legacy/summary',
  'geometry-method': '/lotes/legacy/geometry',
  'geometry-gps': '/lotes/legacy/geometry-gps',
  'geometry-manual': '/lotes/legacy/geometry-manual',

  // AGC: Tasks
  'tasks': '/tasks',
  'new-task': '/tasks/new',
  'task-detail': '/tasks/view',
  'lot-task-detail': '/tasks/view',

  // AGC: Weather
  'weather': '/weather',
  'spray-suitability': '/weather/spray',
  'forecast': '/weather/forecast',

  // AGC: Financial
  'finances': '/finances',
  'expenses': '/expenses',
  'new-expense': '/expenses/new',
  'new-revenue': '/revenues/new',

  // AGC: Settings
  'settings': '/settings',
  'sistema-settings': '/settings/sistema',
  'categorias-settings': '/settings/categorias',
  'tarefas-settings': '/settings/tarefas',
  'maquinario-settings': '/settings/maquinario',
  'culturas-settings': '/settings/culturas',
  'voice-calibration': '/settings/voice-calibration',

  // AGC: Advisors
  'advisor-marketplace': '/advisors',
  'consultant-profile': '/advisors/profile',
  'consultation-request': '/consultations/new',
  'consultations': '/consultations',
  'consultation-detail': '/consultations/view',

  // AGC: Inventory
  'inventory': '/inventory',
  'inputs': '/inventory',
  'purchase-orders': '/pedidos',

  // AGC: Machinery
  'machinery': '/machinery',
  'machine-form': '/machinery/new',
  'machine-detail': '/machinery/view',

  // AGC: Team
  'team-users': '/team',
  'team-roles': '/team/roles',
  'team-permissions': '/team/permissions',
  'team-add-member': '/team/add',
  'member-profile': '/team/member',

  // AGC: Sorteios — user-facing raffle page
  'sorteios': '/sorteios',
}

// AGC: Reverse mapping — URL path → Screen (for LegacyMainMenuAdapter)
export const PATH_TO_SCREEN: Record<string, Screen> = {}
for (const [screen, path] of Object.entries(SCREEN_ROUTES)) {
  // AGC: Only map if not already mapped (first entry wins for duplicates)
  if (!PATH_TO_SCREEN[path]) {
    PATH_TO_SCREEN[path] = screen as Screen
  }
}

/**
 * AGC: useAppNavigate — returns a navigation function with the same signature
 * AGC: as the old onNavigate callbacks: (screen: Screen, data?) => void
 * AGC: Internally maps to React Router navigate() with route state
 */
export function useAppNavigate(): (screen: Screen, data?: Partial<NavigationState>) => void {
  const navigate = useNavigate()

  return useCallback((screen: Screen, data?: Partial<NavigationState>) => {
    const path = SCREEN_ROUTES[screen]
    if (path) {
      navigate(path, { state: data || undefined })
    } else {
      // AGC: Fallback for unmapped screens — use screen name as path
      navigate(`/${screen}`, { state: data || undefined })
    }
    // AGC: Scroll to top on navigation (matches old MainMenu behavior)
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
  }, [navigate])
}

/**
 * AGC: useGoBack — simple back navigation hook
 * AGC: Uses router history when available, falls back to dashboard
 */
export function useGoBack(): () => void {
  const navigate = useNavigate()

  return useCallback(() => {
    // AGC: If there's history, go back; otherwise go to dashboard
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/')
    }
  }, [navigate])
}

/**
 * AGC: pathToScreen — map a URL pathname to a Screen value for legacy adapter
 */
export function pathToScreen(pathname: string): Screen {
  // AGC: Direct match first
  const direct = PATH_TO_SCREEN[pathname]
  if (direct) return direct

  // AGC: Try prefix matching for nested routes
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return 'dashboard'

  // AGC: Build progressively shorter paths to find a match
  for (let i = segments.length; i > 0; i--) {
    const partial = '/' + segments.slice(0, i).join('/')
    const match = PATH_TO_SCREEN[partial]
    if (match) return match
  }

  return 'dashboard'
}
